import type { ModuleManifest } from '@eis/core';
import { insurancePermissions as p } from './permissions';
import { insuranceResources } from './i18n';

/**
 * The insurance module's single manifest — the one integration point with the shell (backbone §2).
 *
 * `nav` entries carry the SAME permission declarations the routes are guarded with, so the menu
 * and the guard can never drift apart (Addendum A.2, enforcement layer 1).
 *
 * `icon` names a Feather glyph (`feather:<glyph>`, SDD-012 U-4): the suite chrome variant draws it
 * from the legacy's icon font, the Berry variant resolves it to the MUI SVG named in
 * `icon-map.json`. The manifest does not know which — that is the shell's registry's job.
 *
 * `section` is the drawer heading the entries group under (Berry layout, §B.8) — presentation
 * only, and filtered with the entries, so a persona who may see neither never sees the heading.
 * The headings are the operating model's phases (architecture §A.5): Produkte is the
 * product-engineering persona's, Gruppen-Policen the operations persona's. A persona holding one
 * and not the other sees one heading, which is the point of splitting them.
 */
export const insuranceModule: ModuleManifest = {
  id: 'insurance',
  basePath: '/insurance',
  routes: () => import('./routes'),
  nav: [
    {
      label: 'insurance:nav.tarifs',
      icon: 'feather:package',
      to: '/insurance/tarifs',
      section: 'insurance:nav.sections.products',
      permissions: [[p.products.view]],
    },
    {
      label: 'insurance:nav.groupTarifs',
      icon: 'feather:layers',
      to: '/insurance/group-tarifs',
      section: 'insurance:nav.sections.products',
      permissions: [[p.groupTariffs.view]],
    },
    {
      label: 'insurance:nav.groupPolicies',
      icon: 'feather:briefcase',
      to: '/insurance/group-policies',
      section: 'insurance:nav.sections.groupPolicies',
      permissions: [[p.groupPolicies.view]],
    },
    {
      label: 'insurance:nav.policies',
      icon: 'feather:box',
      to: '/insurance/policies',
      section: 'insurance:nav.sections.policies',
      permissions: [[p.policies.view]],
    },
    // Leads (SDD-013 R-1): the offers that may become policies, beside Policen — the same
    // section, because a lead is instance data and both operating personas hold it (Q-5).
    {
      label: 'insurance:nav.leads',
      icon: 'feather:file-text',
      to: '/insurance/leads',
      section: 'insurance:nav.sections.policies',
      permissions: [[p.leads.view]],
    },
  ],
  i18nNamespaces: ['insurance'],
  permissionCatalog: p,
  featureFlagPrefix: 'insurance.',
  i18nResources: insuranceResources,
};
